/* eslint-disable @atlaskit/design-system/use-spotlight-package */
import Spotlight from '@atlaskit/onboarding/spotlight';
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';

import { useEffect, useState } from 'react';

import { SPOTLIGHT_DISMISS_EVENT } from '..';

import { HomeSpotlight } from './HomeSpotlight';

import { RovoChatSpotlight } from './RovoChatSpotlight';

import { SwitcherSpotlight } from './SwitcherSpotlight';

import { SPOTLIGHT_WIDTH } from './constants';

import { type JSX } from 'react/jsx-runtime';

export type SpotlightTourProps = {
	userName?: string;
	openRovoPanel?: () => void; // if openRovoPanel function is provided, then rovo is enabled
	toggleSidebar: () => void;
	dismiss: () => void;
	isSideNavCollapsed: () => boolean;
	toggleSwitcher: (toggle: 'open' | 'close') => void;
	isRovoChatOpen: boolean;
	sidebarForcedOpen: boolean;
};

const SpotlightTour = (props: SpotlightTourProps): JSX.Element | null => {
	const {
		userName,
		sidebarForcedOpen,
		openRovoPanel,
		toggleSidebar,
		toggleSwitcher,
		isRovoChatOpen,
		isSideNavCollapsed,
		dismiss,
	} = props;

	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);

	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
	const back = () => setActiveSpotlight((activeSpotlight || 1) - 1);

	const endTour = () => {
		setActiveSpotlight(null);
		dismiss();
	};

	const onOpenRovoPanel = () => {
		openRovoPanel && openRovoPanel();
		endTour();
	};

	const moveFromHomeToSwitcher = () => {
		if (sidebarForcedOpen) {
			toggleSidebar();
		}
		toggleSwitcher('open');
		next();
	};

	const moveFromSwitcherToHome = () => {
		toggleSwitcher('close');
		if (isSideNavCollapsed()) {
			toggleSidebar();
		}
		back();
	};

	const moveFromSwitherToRovo = () => {
		toggleSwitcher('close');
		next();
	};

	const moveFromRovoToSwitcher = () => {
		toggleSwitcher('open');
		back();
	};

	const numTotalSpotlights = openRovoPanel ? 3 : 2;

	// listen for blanket clicks
	useEffect(() => {
		if (typeof document !== 'undefined') {
			document.addEventListener(SPOTLIGHT_DISMISS_EVENT, endTour);
		}

		return () => {
			if (typeof document !== 'undefined') {
				document.removeEventListener(SPOTLIGHT_DISMISS_EVENT, endTour);
			}
		};
	}, [endTour]);

	if (activeSpotlight === null) {
		return null;
	}

	const renderActiveSpotlight = () => {
		const spotlights = [
			<Spotlight
				target="for-you"
				dialogWidth={SPOTLIGHT_WIDTH}
				dialogPlacement="right top"
				shouldWatchTarget
			>
				<HomeSpotlight
					userName={userName}
					spotlightEnd={endTour}
					spotlightNext={moveFromHomeToSwitcher}
					numTotalSpotlights={numTotalSpotlights}
				/>
			</Spotlight>,
			<Spotlight
				target="home-switcher"
				dialogWidth={SPOTLIGHT_WIDTH}
				dialogPlacement="right top"
				shouldWatchTarget
			>
				<SwitcherSpotlight
					spotlightEnd={endTour}
					spotlightNext={moveFromSwitherToRovo}
					spotlightBack={moveFromSwitcherToHome}
					isRovoEnabled={!!openRovoPanel}
					numTotalSpotlights={numTotalSpotlights}
				/>
			</Spotlight>,
		];
		if (openRovoPanel) {
			spotlights.push(
				<Spotlight
					target="rovo-chat"
					dialogWidth={SPOTLIGHT_WIDTH}
					dialogPlacement="bottom right"
					shouldWatchTarget
				>
					<RovoChatSpotlight
						spotlightEnd={endTour}
						spotlightBack={moveFromRovoToSwitcher}
						openRovoPanel={onOpenRovoPanel}
						numTotalSpotlights={numTotalSpotlights}
						isRovoChatOpen={isRovoChatOpen ?? false}
					/>
				</Spotlight>,
			);
		}

		return spotlights[activeSpotlight];
	};

	return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};

export default SpotlightTour;
